import { CustomCard } from '@/components/custom-card';
import { SubheadingSm } from '@/components/custom-titles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatNumberOrDash } from '@/utils/numberFormat';
import { ArrowLeft, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SURFACE, SURFACE_RAISED } from '../constants';
import { HarvestPlacesRegionRow } from '../model';

export interface LocationsTableColumn {
  label: string;
  value: (row: HarvestPlacesRegionRow) => React.ReactNode;
}

interface LocationsTableProps {
  className?: string;
  data?: HarvestPlacesRegionRow[];
  isLoading?: boolean;
  /** Karta sarlavhasi — "Viloyatlar" yoki tanlangan viloyat uchun "Maskanlar". */
  title: string;
  /** Birinchi ustun nomi — "Viloyat nomi" yoki "Maskan nomi". */
  nameLabel: string;
  onRowClick?: (row: HarvestPlacesRegionRow) => void;
  /** Berilsa, sarlavha oldida ortga tugmasi chiqadi. */
  onBack?: () => void;
  /** Qatorlar soni cheklangan bo'lsa (masalan viloyatlar) scroll shart emas. */
  scrollable?: boolean;
  /** Qidiruv maydonini ko'rsatish. Viloyatlar ro'yxati uchun kerak emas. */
  searchable?: boolean;
  /** Sarlavha qatoriga qo'shimcha boshqaruvlar (STIR kiritish). */
  actions?: React.ReactNode;
  /** Nom ustunidan keyingi ustunlar. Berilmasa vazn/shartnoma ustunlari chiqadi. */
  columns?: LocationsTableColumn[];
}

export const LocationsTable = ({
  className,
  data,
  isLoading,
  title,
  nameLabel,
  onRowClick,
  onBack,
  scrollable = true,
  searchable = true,
  actions,
  columns,
}: LocationsTableProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const tableColumns: LocationsTableColumn[] = columns || [
    {
      label: t('physical_weight_t'),
      value: (row) => formatNumberOrDash(row.physical_total_net),
    },
    {
      label: t('conditioned_weight_t'),
      value: (row) => formatNumberOrDash(row.conditioned_total_net),
    },
    {
      label: t('futures'),
      value: (row) => formatNumberOrDash(row.futures_count, ` ${t('ta')}`),
    },
    {
      label: t('forward'),
      value: (row) => formatNumberOrDash(row.forward_count, ` ${t('ta')}`),
    },
    {
      label: t('storage'),
      value: (row) => formatNumberOrDash(row.storage_count, ` ${t('ta')}`),
    },
  ];

  const filteredData = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return data || [];
    return (data || []).filter((row) =>
      String(row.name_uz || '')
        .toLowerCase()
        .includes(query),
    );
  }, [data, search]);

  return (
    <CustomCard
      className={cn('flex w-full flex-col !px-0 !py-0', SURFACE, className)}
    >
      <div
        className={cn(
          'flex items-center justify-between gap-4 px-6 py-4',
          SURFACE_RAISED,
        )}
      >
        <div className="flex items-center gap-3">
          {onBack && (
            <Button
              variant="outline"
              className="rounded-md border-white/20 bg-card p-1"
              onClick={onBack}
            >
              <ArrowLeft className="text-violet-600" />
            </Button>
          )}
          <SubheadingSm title={title} />
        </div>

        <div className="flex items-center gap-3">
          {actions}

          {searchable && (
            <div className="flex h-8 w-full max-w-[320px] items-center gap-2 rounded-md border border-border bg-white/[0.04] px-3 transition-colors focus-within:border-white/25">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('search')}
                className="!px-0 text-white"
              />
            </div>
          )}
        </div>
      </div>

      <div
        className={cn(
          'border-t border-border pb-2',
          scrollable && 'sidebar-scrollbar max-h-[560px] overflow-y-auto',
        )}
      >
        <Table className="w-full">
          <TableHeader className={SURFACE_RAISED}>
            <TableRow>
              <TableHead className="w-[35%] font-medium text-foreground">
                {nameLabel}
              </TableHead>
              {tableColumns.map((column) => (
                <TableHead
                  key={column.label}
                  className="font-medium text-foreground"
                >
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {filteredData.length ? (
              filteredData.map((row, index) => (
                <TableRow
                  key={index}
                  onClick={() => onRowClick?.(row)}
                  className={cn(onRowClick && 'cursor-pointer')}
                >
                  <TableCell
                    className="max-w-[260px] truncate py-2.5 text-foreground"
                    title={row.name_uz || '-'}
                  >
                    {row.name_uz || '-'}
                  </TableCell>
                  {tableColumns.map((column) => (
                    <TableCell
                      key={column.label}
                      className="py-2.5 text-foreground"
                    >
                      {column.value(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={tableColumns.length + 1}
                  className="h-24 text-center text-muted-foreground"
                >
                  {isLoading ? t('loading') : t('no_data')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </CustomCard>
  );
};
